// PlayerMarketTable — the responsive odds-table primitive for Player Goals +
// Player Cards (Phase 3). A sibling of <MarketTable>, NOT a reuse: the player
// layout differs enough to warrant its own component —
//   - column classes carry a `p` suffix (bmc-lbl-wp/np, bmc-cell-wp/np, …);
//   - the narrow layout has NO Value column (wide only);
//   - the VALUE header (always `bmc-val-wp`) shows whenever Best shows, even in
//     narrow — replicating the legacy header/row column mismatch exactly;
//   - headers are all-caps (MARKET / MODEL / VALUE / BEST);
//   - rows key on `data-player` (for `syncPlayerHighlight`, Phase 4);
//   - thresholds are WIDE=340 / SHOW_BEST=220.
// Same measure-before-paint + ResizeObserver + betting-module tag as MarketTable.
// Emits the SAME DOM as the legacy renderer (verified by the A/B). Implied-% runs
// are single template literals (A/B single-text-node rule).

import { useLayoutEffect, useRef, useState } from "react";
import type { PlayerRow } from "@bento/shared";

export interface PlayerMarketTableProps {
  /** Wrapper class: "pg-wrapper" (Goals) / "pc-wrapper" (Cards). */
  wrapperClass: string;
  rows: PlayerRow[];
}

function BestCell({
  colCls,
  best,
}: {
  colCls: string;
  best: PlayerRow["best"];
}) {
  return (
    <div className={`${colCls} bm-best-cell`}>
      <span className="bm-txt">{best.odds.toFixed(2)}</span>
      <div className="bm-badge" style={{ background: best.colour }}>
        <span className="bm-badge-text">{best.abbr}</span>
      </div>
    </div>
  );
}

const WIDE = 340;
const SHOW_BEST = 220;

export default function PlayerMarketTable({
  wrapperClass,
  rows,
}: PlayerMarketTableProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ wide: false, showBest: false });

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const mod = wrapper.closest<HTMLElement>(".placed-module");
    mod?.classList.add("betting-module");

    const measure = () => {
      const w = wrapper.clientWidth;
      setLayout((prev) => {
        const next = { wide: w >= WIDE, showBest: w >= SHOW_BEST };
        return prev.wide === next.wide && prev.showBest === next.showBest
          ? prev
          : next;
      });
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(wrapper);
    }
    return () => {
      ro?.disconnect();
      mod?.classList.remove("betting-module");
    };
  }, []);

  const { wide, showBest } = layout;
  const lbl = wide ? "bmc-lbl-wp" : "bmc-lbl-np";
  const cell = wide ? "bmc-cell-wp" : "bmc-cell-np";
  const bestCol = wide ? "bmc-best-wp" : "bmc-best-np";

  return (
    <div className={`${wrapperClass} bm-wrapper`} ref={wrapperRef}>
      <div className="bm-hdr">
        <div className={lbl} />
        <div className={`${cell} bm-hdr-cell`}>MARKET</div>
        <div className={`${cell} bm-hdr-cell`}>MODEL</div>
        {showBest && <div className="bmc-val-wp bm-hdr-cell">VALUE</div>}
        {showBest && (
          <div className={`${bestCol} bm-hdr-cell bm-right`}>BEST</div>
        )}
      </div>
      <div className="bm-scroll">
        {rows.map((r) => (
          <div
            key={r.player}
            data-player={r.player}
            className={`bm-row${r.alt ? " bm-row--alt" : ""}`}
          >
            <div className={`${lbl} bm-lbl`}>{r.player}</div>
            {wide ? (
              <>
                <div className={`${cell} bm-cell-inline`}>
                  <span className="bm-txt">{r.mktOdds.toFixed(2)}</span>{" "}
                  <span className="bm-prob">{`(${r.mktImp.toFixed(1)}%)`}</span>
                </div>
                <div className={`${cell} bm-cell-inline`}>
                  <span className="bm-txt">{r.modelOdds.toFixed(2)}</span>{" "}
                  <span className="bm-prob">{`(${r.modelImp.toFixed(1)}%)`}</span>
                </div>
                {showBest && (
                  <div
                    className="bmc-val-wp bm-txt bm-center"
                    style={{ color: r.edgeCol }}
                  >
                    {r.edgeStr}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={`${cell} bm-cell-stack`}>
                  <span className="bm-txt">{r.mktOdds.toFixed(2)}</span>
                  <span className="bm-prob">{`${r.mktImp.toFixed(1)}%`}</span>
                </div>
                <div className={`${cell} bm-cell-stack`}>
                  <span className="bm-txt">{r.modelOdds.toFixed(2)}</span>
                  <span className="bm-prob">{`${r.modelImp.toFixed(1)}%`}</span>
                </div>
              </>
            )}
            {showBest && <BestCell colCls={bestCol} best={r.best} />}
          </div>
        ))}
      </div>
    </div>
  );
}
